<template>
	<div class="user-speaker-page pt-5 page">
		<v-skeleton-loader v-if="this.isLoading" v-bind="attrs" type="card" />
		<div v-else>
			<v-card v-if="speakerInfo" class="user-speaker-page__info-wrapper" flat>
				<div class="user-speaker-page__avatar-wrapper">
					<Avatar
						:avatar="speakerInfo.speaker_logo"
						:size="120"
						border-radius="square"
						class="mr-3"
            :defaultAvatar="'speaker'"
            :color="'white'"
					/>
				</div>
				<div class="user-speaker-page__info">
					<h1 class="user-speaker-page__info-username">
						{{ speakerInfo.speaker_name }}
					</h1>
					<p
						v-if="speakerInfo.speaker_company"
						class="user-speaker-page__info-description"
					>
						{{ speakerInfo.speaker_company }}
					</p>
					<p v-if="speakerInfo.speaker_title" class="user-speaker-page__info-description">
						{{ speakerInfo.speaker_title }}
					</p>
				</div>
			</v-card>
			<!-- buttons -->
			<div class="user-speaker-page__buttons d-flex mb-2">
				<a
					v-if="speakerInfo.speaker_website"
					class="user-speaker-page__buttons__button text-decoration-none"
					:href="speakerInfo.speaker_website"
					target="_self"
				>
					<Icon type="link" color="var(--button-icon-tint)" :size="24" />
					<span>Website</span>
				</a>
				<div @click="addToBookmark" class="user-speaker-page__buttons__button u-cursor-pointer">
					<Icon
						:type="bookmarked ? 'bookmark' : 'bookmark_empty'"
						:color="'var(--button-icon-tint)'"
						:size="24"
					/>
					<span>{{ bookmarked ? 'Bookmarked' : 'Bookmark' }}</span>
				</div>
			</div>
			<!-- Speaker bio -->
			<v-card
				v-if="speakerBio && speakerBio.speaker_bio"
				class="user-speaker-page__section"
				flat
			>
				<h3 class="user-speaker-page__section-title mb-3">Bio</h3>
				<p class="user-speaker-page__bio" v-html="speakerBio.speaker_bio"></p>
			</v-card>
			<!-- Speaker socials -->
			<v-card
				v-if="
					socialsInfo &&
					(socialsInfo.speaker_twitter_url ||
						socialsInfo.speaker_linked_in ||
						socialsInfo.speaker_facebook ||
						socialsInfo.speaker_instagram)
				"
				class="user-speaker-page__section"
				flat
			>
				<h3 class="user-speaker-page__section-title mb-3">Social Networks</h3>
				<div
					v-if="socialsInfo.speaker_twitter_url"
					class="user-speaker-page__network-wrapper mt-2"
				>
					<a
						:href="socialsInfo.speaker_twitter_url"
						target="_self"
						class="user-speaker-page__network"
					>
						<Icon color="var(--button-icon-tint)" type="twitter" :size="24" :pr="8" />
						<span class="user-speaker-page__network-link">
							{{ socialsInfo.speaker_twitter_url }}
						</span>
					</a>
				</div>
				<div
					v-if="socialsInfo.speaker_linked_in"
					class="user-speaker-page__network-wrapper mt-2"
				>
					<a
						:href="socialsInfo.speaker_linked_in"
						target="_self"
						class="user-speaker-page__network"
					>
						<Icon color="var(--button-icon-tint)" type="linkedin" :size="24" :pr="8" />
						<span class="user-speaker-page__network-link">
							{{ socialsInfo.speaker_linked_in }}
						</span>
					</a>
				</div>
				<div
					v-if="socialsInfo.speaker_facebook"
					class="user-speaker-page__network-wrapper mt-2"
				>
					<a
						:href="socialsInfo.speaker_facebook"
						target="_self"
						class="user-speaker-page__network"
					>
						<Icon color="var(--button-icon-tint)" type="facebook" :size="24" :pr="8" />
						<span class="user-speaker-page__network-link">
							{{ socialsInfo.speaker_facebook }}
						</span>
					</a>
				</div>
				<div
					v-if="socialsInfo.speaker_instagram"
					class="user-speaker-page__network-wrapper mt-2"
				>
					<a
						:href="socialsInfo.speaker_instagram"
						target="_self"
						class="user-speaker-page__network"
					>
						<Icon color="var(--button-icon-tint)" type="instagram" :size="24" :pr="8" />
						<span class="user-speaker-page__network-link">
							{{ socialsInfo.speaker_instagram }}
						</span>
					</a>
				</div>
			</v-card>
			<!-- Speaker contacts -->

			<v-card
				v-if="contactsInfo && (contactsInfo.speaker_phone || contactsInfo.speaker_email)"
				class="user-speaker-page__section"
				flat
			>
				<h3 class="user-speaker-page__section-title mb-3">Contact</h3>
				<div
					v-if="contactsInfo.speaker_phone"
					class="user-speaker-page__contact-box u-cursor-pointer d-flex"
					@click="copyPhone(contactsInfo.speaker_phone)"
				>
					<Icon color="var(--button-icon-tint)" type="phone" :size="24" :pr="8" />
					<span class="user-speaker-page__phone">
						{{ contactsInfo.speaker_phone }}
					</span>
				</div>
				<a
					v-if="contactsInfo.speaker_email"
					class="user-speaker-page__contact-box text-decoration-none mt-2 d-flex"
					:href="`mailto:${contactsInfo.speaker_email}`"
				>
					<Icon color="var(--button-icon-tint)" type="email" :size="25" :pr="8" />
					<span class="user-speaker-page__email">
						{{ contactsInfo.speaker_email }}
					</span>
				</a>
			</v-card>
			<!-- Speaker rating -->
			<div class="mb-2" v-if="ratingInfo && ratingInfo.rating">
				<Rating @handleTriggerSpeakerRating="handleTriggerSpeakerRating" :rating="ratingInfo.rating" />
			</div>
			<!-- Speaker sessions -->
			<div class="user-speaker-page__sessions-container mb-2">
				<div class="user-speaker-page__sessions-container__session d-flex justify-center">
					<router-link
						:to="{
							name: 'speaker-sessions',
							params: { id: speakerInfo.speaker_id, type: 'future' },
						}"
						class="user-speaker-page__sessions d-flex align-center flex-column justify-center"
					>
						<Icon
							class="mb-2"
							type="remove_schedule"
							color="var(--button-icon-tint)"
							:size="24"
						/>
						<span class="user-speaker-page__sessions-label"> Active Sessions </span>
					</router-link>
				</div>

				<div class="user-speaker-page__sessions-container__session d-flex justify-center">
					<router-link
						:to="{
							name: 'speaker-sessions',
							params: { id: speakerInfo.speaker_id, type: 'past' },
						}"
						class="user-speaker-page__sessions d-flex align-center flex-column justify-center"
					>
						<Icon
							class="mb-2"
							type="add_schedule"
							color="var(--button-icon-tint)"
							:size="24"
						/>
						<span class="user-speaker-page__sessions-label"> Past Sessions </span>
					</router-link>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import searchIcon from '@/assets/images/search.svg'
import Avatar from '@/components/Avatar'
import Rating from '@/components/Event/Rating'
import Dwh from '@/mixins/Dwh'

export default {
	name: 'SpeakerPage',
	mixins: [Dwh],
	components: {
		Avatar,
		Rating,
	},
	computed: {
		...mapGetters('user', ['isLoading', 'speaker']),
		...mapGetters('event', ['event']),
		...mapGetters('app', ['isShowAlert']),
		...mapGetters('auth', ['profile']),
		speakerInfo() {
			return this.getBlockData('speakerInfo', {})
		},
		speakerBio() {
			return this.getBlockData('details', {})
		},
		socialsInfo() {
			return this.getBlockData('socialsBlock', {})
		},
		contactsInfo() {
			return this.getBlockData('contactsBlock', {})
		},
		ratingInfo() {
			return this.getBlockData('rating', {})
		},
		currentUser() {
			return this.profile
		},
	},
	methods: {
		...mapActions('user', ['getSpeaker', 'getSpeakerSessions']),
		...mapActions('app', ['setPageTitle', 'showAlert']),
		...mapActions('event', ['toggleBookmark']),
    handlerBookMark(){
      this.sendTrigger({
        category:'Speakers',
        action:'click_speaker_bookmark',
        module:'Event',
        title: this.speakerInfo.speaker_name,
        id1: this.speakerInfo.speaker_id,
        id2:this.$route.query.event_id,
      })
    },
    handleRemoveBookmark(){
      this.sendTrigger({
        category:'Speakers',
        action:'click_speaker_remove_bookmark',
        module:'Event',
        title: this.speakerInfo.speaker_name,
        id1: this.speakerInfo.speaker_id,
        id2:this.$route.query.event_id,
      })
    },
    handleTriggerSpeakerRating(value) {
      this.sendTrigger({
        category: 'Speakers',
        action: 'click_rating_speaker',
        module: 'Events',
        title: this.speakerInfo.speaker_name,
        id1: this.speakerInfo.speaker_id,
        id2:this.$route.query.event_id,
        data:value
      })
    },
    toggleTriggerView(){
      this.sendTrigger({
        category: 'Speakers',
        action: 'view_speaker_profile',
        module: 'Events',
        title: this.speakerInfo.speaker_name,
        id1: this.speakerInfo.speaker_id,
        id2:this.$route.query.event_id,
      })
    },
		getBlockData(name, fallback = undefined) {
			const block = (this.speaker.speakerView || []).find((b) => b.block_name === name)
			return block ? block.block_data : fallback
		},
		copyPhone(value) {
			if (!this.isShowAlert) {
				const phone = value.replace(/\D/g, '')

				this.$copyText(phone).then(() => {
					const closeTimeout = setTimeout(() => {
						this.hideAlert('')
					}, 3000)

					this.showAlert({ message: 'Phone number copied successfully.', closeTimeout })
				})
			}
		},
    async addToBookmark() {
      const prevStatus = this.bookmarked
      const newStatus = prevStatus === 1 ? 0 : 1

      try {
        if (newStatus === 1) {
          this.handlerBookMark()
        } else {
          this.handleRemoveBookmark()
        }
        this.bookmarked = newStatus
        await this.toggleBookmark({
          user_id: this.currentUser.user_id,
          bookmark_entity_type: this.speakerInfo.bookmark.bookmark_entity_type,
          bookmark_status: newStatus,
          bookmark_entity_id: this.speakerInfo.bookmark.bookmark_entity_id,
        })
      } catch (e) {
        console.error(e)
        this.bookmarked = prevStatus
      }
    },
  },
	data() {
		return {
			bookmarked: null,
			searchIcon,
			successMessage: false,
			errorMessage: false,
			attrs: {
				elevation: 0,
			},
			socialFieldNames: [
				'user_linkedin',
				'speaker_facebook',
				'user_twitter',
				'user_custom_url',
				'speaker_instagram',
				'speaker_website',
				'speaker_company',
				'speaker_logo',
			],
			phoneFieldNames: ['speaker_phone', 'speaker_email'],
		}
	},
	watch: {
		speaker: {
			handler() {
				this.bookmarked = this.speakerInfo.bookmark.is_bookmarked
        this.toggleTriggerView()
			},
		},
	},
	created() {
		const id = +this.$route.params.id
		this.getSpeaker(id)
	},
	mounted() {
		this.setPageTitle('Speaker')
	},
	beforeDestroy() {
		this.setPageTitle('')
	},
}
</script>

<style lang="scss">
.user-speaker-page {
	font-family: 'Inter', sans-serif;
	&__sessions-container {
		display: flex;
		gap: 8px;
		&__session {
			width: 100%;
			height: 75px;
			display: flex;
			border: 1px solid #e2e2e2;
			background-color: #ffffff;
			border-radius: 8px;
		}
	}
	&__buttons {
		display: flex;
		gap: 8px;
		&__button {
			width: 100%;
			max-height: 75px;
			height: 100%;
			padding: 12px;
			border-radius: 8px;
			background-color: #ffffff;
			border: 1px solid #e2e2e2;
			display: flex;
			gap: 8px;
			flex-direction: column;
			justify-content: center;
			align-items: center;
			color: var(--link) !important;
			font-weight: 500;
			font-size: 16px;
			line-height: 100%;
			letter-spacing: 0;
		}
	}

	&__contact-box {
		background-color: #f3f3f6;
		border-radius: 8px;
		padding: 12px;
	}

	&__email {
		color: var(--link);
		font-family: 'Inter', sans-serif;
		font-size: 14px;
		font-weight: 400;
		line-height: 17.07px;
		text-align: left;
		margin: 4px;
	}

	&__phone {
		color: var(--link);
		font-family: 'Inter', sans-serif;
		font-size: 14px;
		font-weight: 400;
		line-height: 17.07px;
		text-align: left;
		margin: 4px;
	}

	&__section-website {
		color: #4a4a4a;
		font-family: 'Inter', sans-serif;
		font-size: 16px;
		font-weight: 700;
		line-height: 19.5px;
		text-align: left;
	}

	&__title {
		font-size: 24px;
		font-weight: 700;
		line-height: 29.26px;
		color: var(--company-position-large);
		margin-bottom: 20px;
	}

	&__info {
		max-width: calc(100% - 144px);
		display: flex;
		justify-content: center;
		flex-direction: column;

		@media (max-width: 480px) {
			max-width: 100%;
		}

		&-wrapper {
			display: flex;
			flex-wrap: nowrap;
			padding: 12px;
			border-radius: 8px !important;
			margin-bottom: 8px;
			border: 1px solid #e2e2e2 !important;

			@media (max-width: 480px) {
				flex-direction: column;
			}
		}

		&-username {
			font-size: 24px;
			font-weight: 700;
			line-height: 100%;
			color: var(--username-category-title);
			margin-bottom: 12px;
			word-break: break-word;
		}

		&-description {
			font-size: 16px;
			line-height: 19.5px;
			color: var(--company-position-large);
			margin-bottom: 4px !important;
			overflow: hidden;
			text-overflow: ellipsis;
			display: -webkit-box;
			-webkit-line-clamp: 1;
			-webkit-box-orient: vertical;
		}

		&-website {
			display: flex;
			flex-wrap: nowrap;
			align-items: flex-start;
			font-size: 13px;
			line-height: 17px;
			font-weight: 600;
			color: var(--link) !important;
			margin-top: 8px;
			text-decoration: unset;

			span {
				overflow: hidden;
				text-overflow: ellipsis;
				display: -webkit-box;
				-webkit-line-clamp: 1;
				-webkit-box-orient: vertical;
			}
		}
	}

	&__avatar-wrapper {
		@media (max-width: 480px) {
			display: flex;
			justify-content: center;
			margin-bottom: 8px;
		}
	}

	&__section {
		align-items: center;
		padding: 12px !important;
		margin-bottom: 8px;
		border-radius: 8px !important;
		border: 1px solid #e2e2e2 !important;
		&-title {
			font-size: 18px;
			font-weight: 700;
			line-height: 100%;
			color: #4a4a4a;
		}
	}

	&__network {
		display: flex;
		flex-wrap: nowrap;
		align-items: center;
		text-decoration: none;
		background-color: #f3f3f6;
		border-radius: 8px;
		padding: 12px;
		cursor: pointer;

		&-title {
			font-size: 14px;
			font-weight: 600;
			line-height: 22px;
			color: var(--company-position-large);
			margin-bottom: 4px;
		}

		&-link {
			font-size: 14px;
			line-height: 17.07px;
			font-weight: 400;
			color: var(--link);
			overflow: hidden;
			text-overflow: ellipsis;
			display: -webkit-box;
			-webkit-line-clamp: 1;
			-webkit-box-orient: vertical;
		}
	}

	&__bio {
		font-size: 14px;
		line-height: 140%;
		color: #4a4a4a !important;
		margin: 0 !important;
		font-weight: 400;
	}

	&__sessions {
		height: 75px;
		text-decoration: none;

		&-icon {
			color: #42a0d8 !important;
			margin-right: 10px;
		}

		&-label {
			font-family: 'Inter', sans-serif;
			font-weight: 500;
			font-size: 16px;
			line-height: 100%;
			letter-spacing: 0;
			color: var(--username-category-title);
		}
	}
}
</style>
